import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// No types, typescript throws an error
// @ts-ignore
import html2pdf from 'html2pdf.js';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { CircularProgress, Tooltip, makeStyles } from '@material-ui/core';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { dynamicTranslate, translate } from 'components/Translation';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import PaymentService from 'services/payments/payment';
import moment from 'moment';
import numeral from 'numeral';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentRequestStatusBOMStyle';
import { TextCell } from 'components/Cells/Cells';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import { BodyM, BodyMBold, BodySBold, DescriptionM, HeadingMBold } from 'components/Typography/Typography';
import { SingleRequestStatus } from 'utils/api/models/payments/singlePaymentRequestStatus';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { PaymentStatus, paymentStatusList, PaymentStatusText } from './constant';
import { BillingDateSelector } from 'views/Bills/BillsList/subcomponents/Filters/BillingDateSelector';
import { now } from 'utils/date/getDate';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { getBOMLabels, getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
import { DATE_TIME, MONTH_YEAR, YYYY_MM_DD } from 'views/Bills/constants';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import { PaymentSummary } from 'components/PaymentMethod';
import classNames from 'classnames';
import { AlternativeSelectWrapper } from 'views/common/AlternativeSelectWrapper';
import { CellWithTooltip } from 'components/CellWithTooltip';
import { ExtractKey } from 'views/PaymentsHistory/constants';
import { Info } from '@material-ui/icons';
import { ErrorCodes } from './constant';
import { roundup } from 'components/PaymentMethod/helper/number';
import { NavigationArrowDropUp } from 'material-ui/svg-icons';
import { white } from 'material-ui/styles/colors';
import { decodeBase64 } from './util';

// @ts-ignore
const useStyles = makeStyles(styles);
export interface Period {
	startDate: moment.Moment;
	endDate: moment.Moment;
}
interface PaginationType {
	pageNumber: number;
	totalPages: number;
	pageSize: number;
	totalElements: number;
}

const PaymentRequestStatusBOM = () => {
	const [loading, setLoading] = useState(false);
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [requestStatus, setReuquestStatus] = useState<SingleRequestStatus[]>([]);
	const [summaryData, setSummaryData] = useState<PaymentSummary>();
	const [showAlternatiiveSelectChild, setShowAlternatiiveSelectChild] = useState(false);
	const [startDateSelected, setStartDateSelected] = useState(true);
	const [endDateSelected, setEndDateSelected] = useState(false);
	const [openDatePicker, setOpenDatePicker] = useState(true);
	const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [transactionData, setTransactionData] = useState<{
		transactionNumber: string;
		dateTime: string;
		status: string;
	}>({ transactionNumber: '', dateTime: '', status: '' });
	const [paginationData, setPaginationData] = useState<PaginationType>({
		pageNumber: 0,
		totalPages: 0,
		pageSize: 10,
		totalElements: 0,
	});

	const dispatch = useDispatch();
	const classes = useStyles();
	const isMounted = useRef(true);
	const labels = useSelector(getBOMLabels);
	const commonLabels = useSelector(getCommonLabels);
	const billsListLabels = labels.billsListLabels;
	const BILLS_SEARCHING_LIMIT = billsListLabels.billsSearchingLimit; // months
	const [dates, setDates] = useState<Period>({
		startDate: moment()
			.subtract(BILLS_SEARCHING_LIMIT, 'months')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});
	const ref: RefObject<HTMLDivElement> = useRef(null);
	const summaryRef: RefObject<HTMLDivElement> = useRef(null);

	useEffect(() => {
		if (ref.current) ref.current.focus();
	}, []);
	const [altSelValue, setAltSelValue] = useState(
		`${moment(now)
			.subtract(BILLS_SEARCHING_LIMIT, 'months')
			.format(MONTH_YEAR)} - ${moment(now).format(MONTH_YEAR)}`
	);
	const [appliedFilters, setAppliedFilters] = useState<{
		paymentPeriod: { startDate: moment.Moment; endDate: moment.Moment };
		paymentStatus: {
			value: string;
			name: string;
			id: string;
		};
	}>({
		paymentPeriod: {
			startDate: moment(now).subtract(BILLS_SEARCHING_LIMIT, 'months'),
			endDate: moment(now),
		},
		paymentStatus: {
			value: '',
			id: '1',
			name: labels.bomStatusLabels.All,
		},
	});
	useEffect(() => {
		setAppliedFilters({
			...appliedFilters,
			paymentPeriod: {
				startDate: dates.startDate,
				endDate: dates.endDate,
			},
		});
	}, [altSelValue]);
	const getPaymentReceipt = (paymentRequestId: string, date: string) => {
		dispatch(showLoadingIndicator());
		PaymentService.getPaymentReceipt(paymentRequestId).subscribe(({ response }) => {
			dispatch(hideLoadingIndicator());
			if (isMounted.current) {
				let data = response?.getPaymentReceiptResponse?.htmlReceiptBase64;
				if (data) {
					const encodedData = decodeBase64(data);
					html2pdf()
						.from(encodedData)
						.set({
							filename: `payment${date}.pdf`,
							margin: 0,
							jsPDF: {
								unit: 'mm',
								format: 'a4',
								orientation: 'portrait',
							},
							html2canvas: {
								height: 1080,
								useCORS: true,
							},
							enableLinks: true,
						})
						.save();
				}
			}
		});
	};

	const getPaymentStatus = (pageSize = paginationData.pageSize, pageNumber = paginationData.pageNumber) => {
		setLoading(true);
		PaymentService.getPaymentsRequestStatus(
			pageNumber,
			pageSize,
			moment(dates.startDate).format(YYYY_MM_DD),
			moment(dates.endDate).format(YYYY_MM_DD),
			appliedFilters.paymentStatus.value,
			searchInputValue.length > 3 ? searchInputValue : ''
		).subscribe(
			({ response }) => {
				setReuquestStatus(response?.transactionDetailsList.content);
				setPaginationData({
					pageNumber: response?.transactionDetailsList?.pageable?.pageNumber,
					pageSize: response?.transactionDetailsList?.pageable?.pageSize,
					totalPages: response?.transactionDetailsList?.totalPages,
					totalElements: response?.transactionDetailsList?.totalElements,
				});
				setLoading(false);
			},
			(error) => {
				setLoading(false);
			}
		);
	};

	useEffect(() => {
		getPaymentStatus();
	}, [selectedAccount, appliedFilters.paymentStatus.value, appliedFilters.paymentPeriod]);
	useEffect(() => {
		const getTransactionDetails = requestStatus.find(
			(request) => request.referenceNumber === transactionData.transactionNumber
		);
		if (getTransactionDetails) {
			setSummaryLoading(true);
			const paymentSummaryItems = getTransactionDetails.billNumberList.map((bills) => {
				return {
					billingId: bills.billingId,
					billNumbers: [bills.billNumber],
				};
			});
			PaymentService.getPaymentSummary({ paymentSummaryItems }).subscribe(
				({ response }) => {
					setSummaryData(response);
					setSummaryLoading(false);
				},
				(error) => {
					setSummaryLoading(false);
				}
			);
		}
	}, [transactionData.transactionNumber]);
	useEffect(() => {
		if (searchInputValue.length > 4) {
			let getData: NodeJS.Timeout;
			getData = setTimeout(() => {
				getPaymentStatus();
			}, 500);
			return () => clearTimeout(getData);
		}
	}, [searchInputValue]);
	const onPageChange = (page: number) => {
		getPaymentStatus(paginationData.pageSize, page);
	};
	const onPageSizeChange = (size: number) => {
		getPaymentStatus(size);
	};
	useEffect(() => {
		if (loading) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	const refreshStatus = (transactionReferenceNumber: string) => {
		setLoading(true);
		PaymentService.getRefreshPayment(transactionReferenceNumber).subscribe(
			({ response }) => {
				let requestStatusList = requestStatus.map((status) => {
					if (status.referenceNumber === transactionReferenceNumber) {
						status.status = response.transactionStatus;
						status.errorCode = response.transactionErrorCode;
						status.errorDescription = response.transactionErrorDescription;
						status.receiptNumber = response.receiptNumber;
						status.paymentRequestId = response.paymentRequestId;
					}
					return status;
				});
				setReuquestStatus(requestStatusList);
				setLoading(false);
			},
			(error) => {
				setLoading(false);
			}
		);
	};
	const getTextFromStatus = (status: string) => {
		switch (status) {
			case PaymentStatus.Pending:
				return PaymentStatusText.Pending;
			case PaymentStatus.Approved:
				return PaymentStatusText.Approved;
			case PaymentStatus.Rejected:
				return PaymentStatusText.Rejected;
			case PaymentStatus.Unreconciled:
				return PaymentStatusText.Unreconciled;
			case PaymentStatus.EXPIRED:
				return PaymentStatusText.Expired;
			default:
				return '';
		}
	};
	const getErrorCodeText = (code: string) => {
		switch (code) {
			case ErrorCodes.SERVER_5XX_ERROR:
				return labels.bomStatusLabels.SERVER_5XX_ERROR;
			case ErrorCodes.UNKNOWN_ERROR:
				return labels.bomStatusLabels.UNKNOWN_ERROR;
			case ErrorCodes.SERVER_4XX_ERROR:
				return labels.bomStatusLabels.SERVER_4XX_ERROR;
			case ErrorCodes.REQUEST_TIMEOUT:
				return labels.bomStatusLabels.REQUEST_TIMEOUT;
			case ErrorCodes.UNAUTHORIZED:
				return labels.bomStatusLabels.UNAUTHORIZED;
			case ErrorCodes.TRANSACTION_REJECTED:
				return labels.bomStatusLabels.TRANSACTION_REJECTED;
			case ErrorCodes.TRANSACTION_DECLINED_TECHNICAL_ERROR:
				return labels.bomStatusLabels.TRANSACTION_DECLINED_TECHNICAL_ERROR;
			case ErrorCodes.TRANSACTION_DECLINED_OTHER_REASON:
				return labels.bomStatusLabels.TRANSACTION_DECLINED_OTHER_REASON;
			case ErrorCodes.TRANSACTION_APPROVAL_DENIED:
				return labels.bomStatusLabels.TRANSACTION_APPROVAL_DENIED;
			case ErrorCodes.INCORRECT_MOBILE_OR_PAYMENT_ALIAS_ID:
				return labels.bomStatusLabels.INCORRECT_MOBILE_OR_PAYMENT_ALIAS_ID;
			case ErrorCodes.TRANSACTION_DENIED_WITH_DIFFERENT_ERROR:
				return labels.bomStatusLabels.TRANSACTION_DENIED_WITH_DIFFERENT_ERROR;
			case ErrorCodes.ESB_FAILURE:
				return labels.bomStatusLabels.ESB_FAILURE;
			case ErrorCodes.PAYMENT_SUBMISSION_FAILURE:
				return labels.bomStatusLabels.PAYMENT_SUBMISSION_FAILURE;
			default:
				return labels.bomStatusLabels.SERVER_4XX_ERROR;
		}
	};

	const viewPaymentSummary = (paymentRequest: SingleRequestStatus) => {
		setIsSummaryModalOpen(true);
		setTransactionData({
			transactionNumber: paymentRequest.referenceNumber,
			dateTime: paymentRequest.timeStamp,
			status: paymentRequest.status,
		});
		if (summaryRef.current) summaryRef.current.focus();
	};
	return (
		<div className={classes.wrapper} id="request-list">
			<BreadcrumbsItem to="/payments/payments-requests">
				{`${translate('path.my.payments')} - ${translate('path.my.payment.request')}`}
			</BreadcrumbsItem>
			<div tabIndex={0} ref={ref} aria-label={labels.bomStatusLabels.requestStatusHeader}>
				<HeadingMBold color="secondary-light" disableMargin>
					{labels.bomStatusLabels.requestStatusHeader}
				</HeadingMBold>
			</div>
			<div className={classes.filtersWrapper}>
				<GridContainer justify="flex-start">
					<GridItem sm={12} md={12} lg={6}>
						<div className={classes.alternativeSelectWrapper}>
							<div>
								<AlternativeSelectWrapper
									showBorder
									dropdownIcon={showAlternatiiveSelectChild ? <ArrowDropUp /> : <ArrowDropDown />}
									renderChildren={showAlternatiiveSelectChild}
									setRenderChildren={setShowAlternatiiveSelectChild}
									type="sm"
									id="period-select"
									label={labels.billsListLabels.billingPeriodFilter}
									value={<div>{altSelValue}</div>}
									onClick={() => {
										setShowAlternatiiveSelectChild(true);
									}}
									items={[]}
									showItems={false}
									selectedItems={[]}
									withHorizontalPadding
								>
									<div className={classes.billPeriodWrapper}>
										<BodySBold>
											{dynamicTranslate(labels.selectLimitText, BILLS_SEARCHING_LIMIT)}
										</BodySBold>
										<BillingDateSelector
											dates={dates}
											setDates={setDates}
											startDateSelected={startDateSelected}
											endDateSelected={endDateSelected}
											setEndDateSelected={setEndDateSelected}
											setStartDateSelected={setStartDateSelected}
											openDatePicker={openDatePicker}
											setOpenDatePicker={setOpenDatePicker}
											setAltSelValue={setAltSelValue}
										/>
									</div>
								</AlternativeSelectWrapper>
							</div>
						</div>
					</GridItem>

					<GridItem sm={12} md={12} lg={6}>
						<div className={classes.alternativeSelectWrapper}>
							<AlternativeSelect
								showBorder
								dropdownIcon={<ArrowDropDown />}
								type="sm"
								id="payment-status-select"
								label={labels.billsListLabels.paymentStatusFilter}
								value={
									<div>
										{appliedFilters.paymentStatus.value
											? labels.bomStatusLabels[
													getTextFromStatus(appliedFilters.paymentStatus.value)
											  ]
											: labels.bomStatusLabels.All}
									</div>
								}
								items={
									paymentStatusList.map((status) => {
										return { ...status, name: labels.bomStatusLabels[status.name] };
									}) as Array<string | IAlternativeSelectItem>
								}
								selectedItems={[appliedFilters.paymentStatus.id]}
								onClick={(item) => {
									setAppliedFilters({
										...appliedFilters,
										paymentStatus: {
											value: item.value,
											name: item.name,
											id: item.id,
										},
									});
								}}
								withHorizontalPadding
							/>
						</div>
					</GridItem>
				</GridContainer>
			</div>

			<AccountFilterTable
				loading={loading}
				totalElements={paginationData.totalElements}
				accounts={[]}
				setAccount={setSelectedAccount}
				searchPlaceHolder={labels.bomStatusLabels.placeHolder}
				searchType="number"
				setSearchInputValue={setSearchInputValue}
				searchInputValue={searchInputValue}
				selectedAccount={selectedAccount}
				showPaginationBottom={true}
				showPaginationTop={false}
				hideOrgFilter={true}
				filtersValue={{}}
				filters={[]}
				onFilterChange={() => {}}
				data={requestStatus}
				selectPanel={{}}
				defaultPageSize={paginationData.pageSize}
				pageSize={paginationData.pageSize}
				pages={paginationData.totalPages}
				page={paginationData.pageNumber}
				onPaggingChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
				columns={
					[
						{
							Header: labels.bomStatusLabels.transNumber,
							sortable: false,
							Cell: ({ original }: { original: SingleRequestStatus }) =>
								original.referenceNumber ? <TextCell text={original.referenceNumber} /> : null,
						},
						{
							Header: labels.bomStatusLabels.billNo,
							sortable: false,
							Cell: ({ original }: { original: SingleRequestStatus }) =>
								original.billNumberList ? (
									<CellWithTooltip
										list={original.billNumberList}
										extractKey={ExtractKey.BILL_NUMBER}
									/>
								) : null,
						},
						{
							Header: labels.bomStatusLabels.dateTime,
							accessor: 'dateTime',
							Cell: ({ original }: { original: SingleRequestStatus }) =>
								original.timeStamp ? (
									<TextCell text={moment(original.timeStamp).format(DATE_TIME)} />
								) : null,
						},
						{
							Header: labels.bomStatusLabels.status,
							accessor: 'status',
							Cell: ({ original }: { original: SingleRequestStatus }) =>
								original.status ? (
									<div className={classes.statusWrapper}>
										<BodyMBold customClassName={classes[getTextFromStatus(original.status)]}>
											{labels.bomStatusLabels[getTextFromStatus(original.status)]}
										</BodyMBold>
										<ConditionalRender
											show={
												original.status === PaymentStatus.Rejected ||
												original.status === PaymentStatus.Pending
											}
											onTrue={() => (
												<Tooltip
													placement="right"
													arrow
													title={
														<BodyM customClassName={classes.toolTipColor}>
															{original.status === PaymentStatus.Rejected
																? getErrorCodeText(original.errorCode)
																: labels.bomStatusLabels.pendingTransaction}
														</BodyM>
													}
												>
													<Info color="primary" className={classes.errorCodeTip} />
												</Tooltip>
											)}
										/>
									</div>
								) : null,
						},
						{
							Header: labels.bomStatusLabels.amt,
							id: 'amount',
							accessor: (row: SingleRequestStatus) => {
								return row.amount.value;
							},
							Cell: ({ original }: { original: SingleRequestStatus }) => (
								<div className={classes.amountCell}>
									<BodyMBold inline>
										{dynamicTranslate(commonLabels.price_LTR, roundup(original.amount.value))}
									</BodyMBold>{' '}
								</div>
							),
						},
						{
							Header: <div className={classes.actionHeader}>{labels.bomStatusLabels.act}</div>,
							accessor: 'amount',
							sortable: false,
							Cell: ({ original }: { original: SingleRequestStatus }) => (
								<div className={classes.actionCell}>
									<div
										tabIndex={0}
										aria-label={dynamicTranslate(
											labels.bomStatusLabels[
												original.status == PaymentStatus.Pending
													? 'refreshStatusLblEnter'
													: 'refreshStatusLbl'
											],
											original.referenceNumber,
											original.billNumberList[0].billNumber,
											getTextFromStatus(original.status)
										)}
										className={classNames({
											[classes.actionButton]: true,
											[classes.refresh]: original.status == PaymentStatus.Pending,
											[classes.refreshDisabled]: original.status != PaymentStatus.Pending,
										})}
										data-testid="refreshData"
										onClick={() =>
											original.status == PaymentStatus.Pending &&
											refreshStatus(original.referenceNumber)
										}
										onKeyDown={(e) => {
											if (e.keyCode === 13 && original.status == PaymentStatus.Pending) {
												refreshStatus(original.referenceNumber);
											}
										}}
									></div>
									<div
										tabIndex={0}
										aria-label={dynamicTranslate(
											labels.bomStatusLabels.viewDetailsLbl,
											original.referenceNumber,
											original.referenceNumber,
											original.billNumberList[0].billNumber
										)}
										className={classNames({
											[classes.viewData]: true,
											[classes.actionButton]: true,
										})}
										data-testid="viewData"
										onClick={() => viewPaymentSummary(original)}
										onKeyDown={(e) => {
											if (e.keyCode === 13) {
												viewPaymentSummary(original);
											}
										}}
									></div>
									<div
										tabIndex={0}
										aria-label={dynamicTranslate(
											labels.bomStatusLabels[
												original.status == PaymentStatus.Pending
													? 'downloadLblEnter'
													: 'downloadLbl'
											],
											original.referenceNumber,
											original.referenceNumber,
											original.billNumberList[0].billNumber,
											getTextFromStatus(original.status)
										)}
										className={classNames({
											[classes.actionButton]: true,
											[classes.viewPDF]: original.status == PaymentStatus.Approved,
											[classes.viewPDFDisabled]: original.status != PaymentStatus.Approved,
										})}
										data-testid="downloadPdf"
										onClick={() =>
											original.status == PaymentStatus.Approved &&
											getPaymentReceipt(original.paymentRequestId, original.timeStamp)
										}
										onKeyDown={(e) => {
											if (e.keyCode === 13 && original.status == PaymentStatus.Approved) {
												getPaymentReceipt(original.paymentRequestId, original.timeStamp);
											}
										}}
									></div>
								</div>
							),
						},
					].filter(Boolean) as Array<any>
				}
			/>
			<CustomDialog
				header={labels.bomStatusLabels.transDetails}
				isOpen={isSummaryModalOpen}
				width={500}
				hideDialog={() => {
					setIsSummaryModalOpen(false);
					if (ref.current) ref.current.focus();
				}}
				dialogBody={
					<ConditionalRender
						show={summaryLoading && !!summaryData}
						onTrue={() => (
							<div className={classes.loadingWrapper}>
								<CircularProgress />
							</div>
						)}
						onFalse={() =>
							summaryData?.data?.paymentSummaryMonthlyItems &&
							summaryData?.data?.paymentSummaryMonthlyItems?.length > 0 ? (
								<div className={classes.summaryWrapper} tabIndex={0} ref={summaryRef}>
									<div className={classes.trasactionWrapper}>
										<DescriptionM>{labels.bomStatusLabels.transNo}</DescriptionM>
										<DescriptionM>{transactionData.transactionNumber}</DescriptionM>
									</div>
									<div className={classes.dateStatus}>
										<div>
											<BodyMBold>{labels.bomStatusLabels.dateTime}</BodyMBold>
											<DescriptionM dir="ltr">
												{moment(transactionData.dateTime).format(DATE_TIME)}
											</DescriptionM>
										</div>
										<div>
											<BodyMBold>{labels.bomStatusLabels.status}</BodyMBold>
											<div>
												<BodyMBold
													customClassName={classes[getTextFromStatus(transactionData.status)]}
												>
													{labels.bomStatusLabels[getTextFromStatus(transactionData.status)]}
												</BodyMBold>
											</div>
										</div>
									</div>
									{summaryData?.data.paymentSummaryMonthlyItems.map((monthlyData) => {
										return (
											<div className={classes.dateStatus}>
												<div>
													<BodyMBold>{`${moment(monthlyData.month, 'MM').format('MMM')}-${
														monthlyData.year
													}`}</BodyMBold>
													{monthlyData.paymentSummaryBillDetails.map((bills) => {
														return (
															<DescriptionM key={bills.billNumber}>
																{dynamicTranslate(
																	labels.bomStatusLabels.billNoVal,
																	bills.billNumber
																)}
															</DescriptionM>
														);
													})}
												</div>
												<div>
													<BodyMBold>{`${dynamicTranslate(
														commonLabels.price_LTR,
														roundup(monthlyData.totalAmount.value)
													)}`}</BodyMBold>
													{monthlyData.paymentSummaryBillDetails.map((bills) => {
														return (
															<DescriptionM key={bills.billNumber}>{`${dynamicTranslate(
																commonLabels.price_LTR,
																roundup(bills.amount.value)
															)}`}</DescriptionM>
														);
													})}
												</div>
											</div>
										);
									})}
									<div className={classes.dateStatus}>
										<div>
											<BodyMBold>{labels.bomStatusLabels.totalPay}</BodyMBold>
										</div>
										<div>
											<BodyMBold>{`${dynamicTranslate(
												commonLabels.price_LTR,
												roundup(summaryData?.data.totalAmount.value)
											)}`}</BodyMBold>

											<DescriptionM>{labels.bomStatusLabels.vatInclusive}</DescriptionM>
										</div>
									</div>
								</div>
							) : (
								<div className={classes.loadingWrapper}>
									<CircularProgress />
								</div>
							)
						}
					/>
				}
			/>
		</div>
	);
};

export default PaymentRequestStatusBOM;
